import React, { useState, useMemo, useCallback } from 'react';
import {
  Typography,
  Box,
  Button,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { useIntl } from 'react-intl';
import { SelectField } from '../../../../../Components/Field';
import { TypedFormFieldValue } from '../../../../../Components/Form/Form.types';
import { StepProps } from './types';
import { StepButtonBar } from './StepButtonBar';
import OtherRequirementInfo from './OtherRequirementInfo';

const PRIMARY = '#c4122f';

interface Step7ResponsibleProps extends StepProps {
  complianceUsers: { id: number; name: string }[];
  otherRequirementInfo: any;
  setOtherRequirementInfo: (any) => void;
}

const Step7Responsible: React.FC<Step7ResponsibleProps> = ({
  currentStepName,
  onNext,
  onBack,
  onCancel,
  currentData,
  isFirstStep,
  complianceUsers,
  otherRequirementInfo,
  setOtherRequirementInfo,
}) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const paperPadding = isMobile ? theme.spacing(3) : theme.spacing(8);

  const [hasReminder, setHasReminder] = useState<boolean | null>(
    currentData.hasReminder ?? false
  );
  const [selectedResponsible, setSelectedResponsible] = useState<
    { value: number; label: string }[]
  >(currentData.responsible || []);

  const selectOptions = useMemo(
    () =>
      complianceUsers.map((u) => ({
        value: u.id,
        label: u.name,
      })),
    [complianceUsers],
  );

  const handleResponsibleChange = useCallback(
    (
      val:
        | TypedFormFieldValue<number>
        | TypedFormFieldValue<number>[]
        | undefined,
    ) => {
      if (Array.isArray(val)) {
        const responsibles = val.map((v) => ({
          value: v.value,
          label: v.label || '',
        }));
        setSelectedResponsible(responsibles);
      } else if (val === undefined || val === null) {
        setSelectedResponsible([]);
      }
    },
    [],
  );

  const handleNext = () => {
    if (hasReminder !== null) {
      if (hasReminder && selectedResponsible.length === 0) {
        return;
      }
      onNext({
        hasReminder,
        responsible: selectedResponsible,
      });
    }
  };

  const isValid = hasReminder !== null && (hasReminder === false || selectedResponsible.length > 0);

  const responsibleError = hasReminder === true && selectedResponsible.length === 0;

  return (
    <Box style={{ width: '100%' }}>
      <Box
        style={{
          display: 'flex',
          width: '100%',
          gap: '10px',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'stretch',
        }}
      >
        <Box
          style={{
            width: isMobile ? '100%' : 'calc(50% - 5px)',
            flexShrink: 0,
            padding: paperPadding,
            backgroundColor: '#f7f0f1',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <Box
            style={{
              display: 'flex',
              gap: theme.spacing(2),
              maxWidth: '300px',
              width: '100%',
              marginBottom: theme.spacing(3),
            }}
          >
            {/* YES Button */}
            <Button
              onClick={() => setHasReminder(true)}
              variant={hasReminder === true ? 'contained' : 'outlined'}
              style={{
                flex: 1,
                padding: theme.spacing(2),
                backgroundColor: hasReminder === true ? PRIMARY : 'transparent',
                borderColor: PRIMARY,
                color: hasReminder === true ? '#fff' : PRIMARY,
                fontWeight: 600,
              }}
            >
              {formatMessage({ id: 'law-portal.evalution.yes', defaultMessage: 'Yes' })}
            </Button>

            {/* NO Button */}
            <Button
              onClick={() => {
                setHasReminder(false);
                setSelectedResponsible([]); 
              }}
              variant={hasReminder === false ? 'contained' : 'outlined'}
              style={{
                flex: 1,
                padding: theme.spacing(2),
                backgroundColor: hasReminder === false ? PRIMARY : 'transparent',
                borderColor: PRIMARY,
                color: hasReminder === false ? '#fff' : PRIMARY,
                fontWeight: 600,
              }}
            >
              {formatMessage({ id: 'law-portal.evalution.no', defaultMessage: 'No' })}
            </Button>
          </Box>

          {/* Responsible Field - Conditional */}
          {hasReminder === true && (
            <Box
              style={{
                width: '100%',
                maxWidth: '300px',
              }}
            >
              {/* <Typography
                variant="body2"
                style={{
                  fontWeight: 600,
                  marginBottom: '8px',
                  fontSize: '14px',
                }}
              >
                {formatMessage({
                  id: 'utils.filters.complianceUsers',
                  defaultMessage: 'Responsible',
                })}
              </Typography> */}
              <SelectField
                name="responsible"
                placeholder={formatMessage({
                  id: 'utils.filters.complianceUsers',
                  defaultMessage: 'Responsible',
                })}
                options={selectOptions}
                errorTexts={[]}
                isMulti={true}
                initialState={
                  selectedResponsible.length > 0 ? selectedResponsible : undefined
                }
                onChange={handleResponsibleChange}
                menuPlacement="auto"
                maxMenuHeight={210}
              />
            </Box>
          )}
        </Box>

        <OtherRequirementInfo otherRequirementStepInfo={otherRequirementInfo} currentStepName={currentStepName} onUpdate={setOtherRequirementInfo} />
      </Box>

      <StepButtonBar
        onCancel={onCancel}
        onBack={onBack}
        onNext={handleNext}
        nextLabel="form.next"
        nextDisabled={!isValid}
        formatMessage={formatMessage}
        showBack={true}
      />
    </Box>
  );
};

export default Step7Responsible;
